
// Fix: Added React import to resolve missing 'React' namespace errors in type definitions
import React, { useState, useMemo, useCallback } from 'react';
import { Node, NodeType, AppState, PathPoint } from '../types';
import { getZoneCenter } from '../utils/geometry';

// Tinklelio dydis erdviniam indeksavimui (pikseliais)
const GRID_SIZE = 100;

export const useCanvasInteractions = (
  state: AppState, 
  dispatch: any, 
  exportAreaRef: React.RefObject<HTMLDivElement | null>,
  transformRef: any
) => {
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);
  const [zoneStart, setZoneStart] = useState<{ x: number, y: number } | null>(null);

  // Erdvinis indeksas mazgams: gridKey -> Node[]
  const spatialNodeMap = useMemo(() => {
    const map = new Map<string, Node[]>();
    state.nodes.forEach(node => {
      const gx = Math.floor(node.x / GRID_SIZE);
      const gy = Math.floor(node.y / GRID_SIZE);
      const key = `${gx},${gy}`;
      const list = map.get(key) || [];
      list.push(node);
      map.set(key, list);
    });
    return map;
  }, [state.nodes, state.layoutVersion]);

  const getMouseWorldPos = useCallback((e: React.MouseEvent | MouseEvent) => {
    const rect = exportAreaRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const scale = transformRef.current?.instance.transformState.scale || 1;
    return { 
      x: (e.clientX - rect.left) / scale, 
      y: (e.clientY - rect.top) / scale 
    };
  }, [exportAreaRef, transformRef]);

  const snapTarget = useMemo(() => {
    if (!cursorPos || (state.editMode !== 'DRAW_PATH' && state.editMode !== 'DRAW_ARCH_PATH')) return null;
    
    // Tikriname tik kaimyninius tinklelio langelius (iš viso 9)
    const gx = Math.floor(cursorPos.x / GRID_SIZE);
    const gy = Math.floor(cursorPos.y / GRID_SIZE);
    
    let nearestNode: Node | null = null;
    let minDist = 20; // Max snapping atstumas

    for (let x = gx - 1; x <= gx + 1; x++) {
      for (let y = gy - 1; y <= gy + 1; y++) {
        const nodesInGrid = spatialNodeMap.get(`${x},${y}`);
        if (nodesInGrid) {
          // Naudojame for...of vietoj forEach, kad TypeScript teisingai suprastų priskyrimą
          for (const n of nodesInGrid) {
            const d = Math.sqrt(Math.pow(n.x - cursorPos.x, 2) + Math.pow(n.y - cursorPos.y, 2));
            if (d < minDist) {
              minDist = d;
              nearestNode = n;
            }
          }
        }
      }
    }

    if (nearestNode) {
      return { x: nearestNode.x, y: nearestNode.y, id: nearestNode.id, type: 'NODE' as const };
    }
    
    // Zonas tikriname tik jei neradome mazgo
    if (state.editMode === 'DRAW_PATH') {
      const zone = state.zones.find(z => (z.rects || []).some(r => 
        cursorPos.x >= r.x && cursorPos.x <= r.x + r.w && cursorPos.y >= r.y && cursorPos.y <= r.y + r.h
      ));
      if (zone) {
        const center = getZoneCenter(zone);
        return { x: center.x, y: center.y, id: zone.id, type: 'ZONE' as const, zoneType: zone.type };
      }
    }
    return null;
  }, [cursorPos, state.editMode, spatialNodeMap, state.zones]);

  const handleInteractionStart = (e: React.MouseEvent) => {
    const pos = getMouseWorldPos(e);
    if (!pos) return;

    if (state.editMode === 'ADD_ZONE' || state.editMode === 'REGION_DELETE') {
      setZoneStart(pos);
    }
  };

  return {
    cursorPos,
    setCursorPos,
    zoneStart,
    setZoneStart,
    getMouseWorldPos,
    snapTarget,
    handleInteractionStart
  };
};